import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  submitForApproval, 
  approveTender, 
  rejectTender, 
  getPendingApprovals,
  ApproveTenderRequest,
  RejectTenderRequest
} from '../api/approval';
import { notifications } from '@mantine/notifications';

// Submit tender for approval
export const useSubmitForApproval = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => submitForApproval(tenderId),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Tender submitted for approval successfully',
        color: 'green',
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tenders'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to submit tender for approval',
        color: 'red',
      });
    },
  });
};

// Approve tender
export const useApproveTender = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ApproveTenderRequest) => approveTender(tenderId, data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Tender approved successfully',
        color: 'green',
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tenders'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve tender',
        color: 'red',
      });
    },
  });
};

// Reject tender
export const useRejectTender = (tenderId: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: RejectTenderRequest) => rejectTender(tenderId, data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Tender rejected successfully',
        color: 'green',
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tenders'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject tender',
        color: 'red',
      });
    },
  });
};

// Get pending approvals (includes both tenders and evaluation reports)
export const usePendingApprovals = () => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['pendingApprovals'],
    queryFn: getPendingApprovals,
    staleTime: 30000, // 30 seconds
  });
}; 